import React, { useRef } from "react";
import { useEffect } from "react";
import { useState } from "react";
import { Box, Button, Container, Divider, Typography } from "@mui/material";
import RateYourExperience from "./RateYourExperience.tsx";
import QuestionsBank from "./QuestionsBank.tsx";
import OtherDetailsScoreCard from "./OtherDetailsScoreCard.tsx";
import { ApiDeletePhotos } from "../../../apiService/Core/Apicall.tsx";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { CustomUploadProfilePic } from "../../../apiService/Core/CustomUploadProfilePic.tsx";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useSelector } from "react-redux";
import {
  validateStep1,
  validateStep2,
  validateStep3,
  type ValidationError,
} from "../ScoreCardValidation/ValidationSchema.tsx";
import ErrorDialog from "../ScoreCardValidation//ErrorDialog.tsx";
import "./CreateOrEditScoreCard.css";
import SyncIcon from "@mui/icons-material/Sync";
import {
  ApiCallGetReportDetails,
  ApiScoreCardStep1,
  ApiScoreCardStep2,
  ApiScoreCardStep3,
} from "../../../apiService/Core/CustomApiManageReports.tsx";

export default function CreateOrEditScoreCard() {
  const fileTypeMap: { [key: string]: string } = {
    taskPhotos: "report_task_photos",
    invoiceReceipt: "report_invoice_photos",
    checkInFile: "report_location_photos",
    checkOutFile: "report_location_photos",
    questionFiles: "report_question_answer",
    shopperBagImages: "shopper_bag_image"
  };

  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [currentCategoryIndex, setCurrentCategoryIndex] = useState(0);
  const [date, setDate] = useState<any>(null);
  const [ratings, setRatings] = useState<Record<string, number>>({});
  const [uploadedFiles, setUploadedFiles] = useState<any>({
    taskPhotos: [],
    invoiceReceipt: [],
    checkInFile: [],
    checkOutFile: [],
    questionFiles: {},
    shopperBagImages: []
  });

  const [invoice, setInvoice] = useState({
    food: 0,
    bar: 0,
    gaming: 0,
    total: 0,
  });

  // Add state for text inputs to ensure they're controlled components
  const [textInputs, setTextInputs] = useState<any>({});
  const [staffMemberDesc, setStaffMemberDesc] = useState("");
  const [addOnQuestions, setAddOnQuestions] = useState<any>({});

  const [reportDetails, setReportDetails] = useState<{
    starRatingResponseData: any[];
    briefJsonDataResponse: any[];
    addOnQuestion: any[];
    questions: any[];
    reportHeader: any;
    taskPhotos: any;
    invoicePhotos: any;
    reportOtherDetails: any;
  } | null>(null);
  const { allocationId } = useParams<{ allocationId: string }>();

  // Add state for validation errors
  const [validationErrors, setValidationErrors] = useState<ValidationError[]>(
    []
  );
  const [showValidationErrors, setShowValidationErrors] = useState(false);
  const location = useLocation();
  const [answers, setAnswers] = useState<any>({});
  const searchParams = new URLSearchParams(location.search);
  const clientGroup = searchParams.get('clientGroup');

  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = ""; // Some browsers require returnValue to be set
      localStorage.removeItem(`isScoreCardEdited_${allocationId}`);
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, []);

  const hasConfirmedRef = useRef(false);

  // Function to save form data to localStorage

  const saveFormDataToLocalStorage = () => {
    if (!allocationId) return;

    const formData = {
      date: date ? date.toISOString() : null,
      ratings,
      textInputs,
      staffMemberDesc,
      uploadedFiles,
      invoice,
      answers,
      currentStep,
      currentCategoryIndex,
      reportDetails,
    };

    localStorage.setItem(`scorecard_${allocationId}`, JSON.stringify(formData));
    localStorage.setItem(
      `isScoreCardEdited_${allocationId}`,
      JSON.stringify(true)
    );
    console.log("Form data saved to localStorage");
  };

  // Save form data whenever any relevant state changes
  useEffect(() => {
    if (allocationId && reportDetails) {
      saveFormDataToLocalStorage();
    }
  }, [
    date,
    ratings,
    textInputs,
    staffMemberDesc,
    uploadedFiles,
    invoice,
    answers,
    currentStep,
    currentCategoryIndex,
  ]);

  // 1️⃣ First load localStorage if available
  const loadFormDataFromLocalStorage = () => {
    const savedFormData = localStorage.getItem(`scorecard_${allocationId}`);
    if (savedFormData) {
      try {
        const parsedData = JSON.parse(savedFormData);

        if (parsedData.date) setDate(new Date(parsedData.date));
        if (parsedData.ratings) setRatings(parsedData.ratings);
        if (parsedData.textInputs) setTextInputs(parsedData.textInputs);
        if (parsedData.addOnQuestions)
          setAddOnQuestions(parsedData.addOnQuestions);
        if (parsedData.staffMemberDesc)
          setStaffMemberDesc(parsedData.staffMemberDesc);
        if (parsedData.uploadedFiles)
          setUploadedFiles(parsedData.uploadedFiles);
        if (parsedData.invoice) setInvoice(parsedData.invoice);
        if (parsedData.answers) setAnswers(parsedData.answers);
        if (parsedData.currentStep) setCurrentStep(parsedData.currentStep);
        if (parsedData.currentCategoryIndex)
          setCurrentCategoryIndex(parsedData.currentCategoryIndex);
        if (parsedData.reportDetails)
          setReportDetails(parsedData.reportDetails);

        console.log("✅ Form data restored from localStorage");
      } catch (error) {
        console.error("Error parsing saved form data:", error);
      }
    }
  };

  // 2️⃣ Always call API to get latest data and update localStorage
  const fetchData = async () => {
    if (!allocationId) return;

    try {
      const data = `?allocationId=${allocationId}`;
      const response = await ApiCallGetReportDetails(data, clientGroup);

      localStorage.setItem("allocationId", allocationId);
      const isScoreCardEdited = JSON.parse(
        localStorage.getItem(`isScoreCardEdited_${allocationId}`) || "false"
      );
      console.log(isScoreCardEdited); // true or false (actual boolean)

      if (isScoreCardEdited) {
        loadFormDataFromLocalStorage();
        return;
      }
      // Set latest data into state
      setReportDetails(response.data);

      setRatings(
        response.data.starRatingResponseData.reduce(
          (acc, item) => ({
            ...acc,
            [item.reportStarRatingId]: item.starRatingScore,
          }),
          {}
        )
      );

      setTextInputs(
        response.data.briefJsonDataResponse.reduce(
          (acc, item) => ({
            ...acc,
            [item.reportBriefJsonId]: item.briefJsonAnswer || "",
          }),
          {}
        )
      );

      setAddOnQuestions(
        response.data.addOnQuestion.reduce(
          (acc, item) => ({
            ...acc,
            [item.reportAddOnQuestionId]: item.addOnQuestionValue || "",
          }),
          {}
        )
      );

      setStaffMemberDesc(response.data.reportHeader.staffMemberDesc);

      const checkInPhotos =
        response.data.reportHeader?.checkInPhotoUrl?.split(", ") || [];
      const checkOutPhotos =
        response.data.reportHeader?.checkOutPhotoUrl?.split(", ") || [];
      const shopperBagPhotos = response.data.reportHeader?.shopperBagImages?.split(", ") || [];

      const taskPhotoData =
        response.data.taskPhotos?.map((photo) => ({
          reportHdrImageId: photo.reportHdrImageId,
          imageUrl: photo.imageUrl,
        })) || [];

      const invoicePhotos =
        response.data.invoicePhotos?.map((photo) => ({
          reportHdrInvoiceId: photo.reportHdrInvoiceId,
          imageUrl: photo.imageUrl,
        })) || [];

      setUploadedFiles((prev) => ({
        ...prev,
        checkInFile: checkInPhotos,
        checkOutFile: checkOutPhotos,
        taskPhotos: taskPhotoData,
        invoiceReceipt: invoicePhotos,
        shopperBagImages: shopperBagPhotos
      }));

      if (response.data.reportHeader?.inspectionDateTime) {
        setDate(new Date(response.data.reportHeader.inspectionDateTime));
      }

      const answersMap: Record<string, any> = {};
      response.data.questions.forEach((category: any) => {
        category.questions?.forEach((question: any) => {
          answersMap[question.questionId] = {
            isImageValid: true,
            reportDtlId: question.reportDtlId,
            value: question.value,
            score: Number(question.score) || 0,
            comment: question.comment || "",
            isAnswered: Boolean(question.isAnswered),
            photoUrl: question.photoUrl || "",
          };
        });
      });

      setAnswers(answersMap);

      // // After a slight delay (to wait for all setStates), save fresh data to localStorage
      // setTimeout(() => {
      //   saveFormDataToLocalStorage();
      //   console.log("✅ Saved fresh API data to localStorage");
      // }, 100);
    } catch (error) {
      console.error("Error fetching visit times:", error);
    }
  };

  useEffect(() => {
    fetchData();
    console.log(addOnQuestions);
  }, [allocationId]); // Depend on allocationId so it refetches when it changes

  console.log(reportDetails);

  useEffect(() => {
    // if (reportDetails?.reportHeader?.inspectionDateTime) {
    //   setDate(new Date(reportDetails?.reportHeader?.inspectionDateTime));
    // }
    localStorage.setItem("currentStep", currentStep.toString());
    localStorage.setItem("ActiveTask", currentStep.toString());
  }, [reportDetails, currentStep]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        localStorage.removeItem("currentStep");
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  const handleTextChange = (field: string, value: string) => {
    setTextInputs((prev) => {
      const updatedState = { ...prev, [field]: value };
      return updatedState;
    });
    console.log("Updated TextInputs:", { ...textInputs, [field]: value }); // Debugging
  };

  const handleAddOnQuestionChange = (field: string, value: string) => {
    setAddOnQuestions((prev) => {
      const updatedState = { ...prev, [field]: value };
      return updatedState;
    });
  };
  console.log(addOnQuestions);

  const handleAnswerChange = (questionId: any, value: any) => {
    setAnswers((prev) => ({
      ...prev,
      [questionId]: {
        ...(prev[questionId] || {}), // existing values (if any)
        ...value, // override or add new values
      },
    }));
  };

  const handleCommentChange = (questionId: number, comment: string) => {
    setAnswers((prev) => ({
      ...prev,
      [`comment_${questionId}`]: comment,
    }));
  };

  const totalSteps = 3;

  const handleRatingChange = (service: string, value: number) => {
    setRatings((prev) => ({
      ...prev,
      [service]: value,
    }));
  };

  const handleFileUpload = async (
    event: React.ChangeEvent<HTMLInputElement>,
    section: string,
    questionId?: number,
    reportDetailId?: number
  ) => {
    if (!event.target.files || event.target.files.length === 0) return;

    const newFiles = Array.from(event.target.files);

    try {
      for (const file of newFiles) {
        const formData = new FormData();
        formData.append("file", file);

        if (fileTypeMap[section]) {
          formData.append("fileType", fileTypeMap[section]);
        }
        formData.append(
          "reportHeaderId",
          reportDetails?.reportHeader.reportHeaderId
        );
        if (section === "questionFiles" && questionId !== undefined) {
          formData.append("reportDtlId", reportDetailId?.toString() || "");
        }

        // Call API for each file
        const response = await CustomUploadProfilePic(formData);
        console.log(response.location);

        toast.success("File uploaded successfully");

        // Extract reportHdrImageId from response, update based on your actual API response shape
        const uploadedUrl = response.location;
        const newImageId = reportDetails?.reportHeader.reportHeaderId; // Make sure this exists in your response

        // Update state with new uploaded file
        setUploadedFiles((prevState: any) => {
          if (section === "questionFiles" && questionId !== undefined) {
            const existingFiles = prevState.questionFiles[questionId] || [];
            const updatedFiles = [...existingFiles, uploadedUrl];

            setAnswers((prev) => {
              const existingAnswer = prev[questionId] || {};
              return {
                ...prev,
                [questionId]: {
                  ...existingAnswer,
                  photoUrl: updatedFiles.join(","),
                  reportDtlId: reportDetailId || existingAnswer.reportDtlId,
                },
              };
            });

            return {
              ...prevState,
              questionFiles: {
                ...prevState.questionFiles,
                [questionId]: updatedFiles,
              },
            };
          }

          // ✅ Corrected section for taskPhotos
          if (section === "taskPhotos") {
            const updatedTaskPhotos = [
              ...(prevState.taskPhotos || []),
              {
                reportHdrImageId: newImageId,
                imageUrl: uploadedUrl,
              },
            ];

            return {
              ...prevState,
              taskPhotos: updatedTaskPhotos,
            };
          }

          if (section === "invoiceReceipt") {
            const updatedinvoiceReceipt = [
              ...(prevState.invoiceReceipt || []),
              {
                reportHdrImageId: newImageId,
                imageUrl: uploadedUrl,
              },
            ];

            return {
              ...prevState,
              invoiceReceipt: updatedinvoiceReceipt,
            };
          }

          // For others that use string URLs
          return {
            ...prevState,
            [section]: [...(prevState[section] || []), uploadedUrl],
          };
        });
      }
    } catch (error) {
      console.error("Error uploading file:", error);
      toast.error("Error uploading file");
    }
  };

  const handleRemoveFile = async (
    section: string,
    reportId: number, // This is reportHdrImageId or reportHdrInvoiceId
    imageUrl?: string,
    questionId?: number
  ) => {
    let payload: any = {};

    if (section === "taskPhotos") {
      payload = {
        type: "task_photos",
        reportHdrImageId: reportId,
      };
    } else if (section === "invoiceReceipt") {
      payload = {
        type: "invoice",
        reportHdrInvoiceId: reportId,
      };
    }

    try {
      if (section === "taskPhotos" || section === "invoiceReceipt") {
        await ApiDeletePhotos(payload);
        toast.success("File removed successfully!");
      }

      // Update uploadedFiles
      setUploadedFiles((prev) => {
        const updated = { ...prev };

        // Handle questionFiles separately
        if (section === "questionFiles" && questionId !== undefined) {
          const updatedFiles =
            prev.questionFiles[questionId]?.filter(
              (file: string) => file !== imageUrl
            ) || [];

          // Update the answers state with the new photoUrl string
          setAnswers((prev) => ({
            ...prev,
            [questionId]: {
              ...prev[questionId],
              photoUrl: updatedFiles.length > 0 ? updatedFiles.join(",") : "", // Join back to comma-separated string
            },
          }));

          // Return the updated uploadedFiles state
          return {
            ...prev,
            questionFiles: {
              ...prev.questionFiles,
              [questionId]: updatedFiles,
            },
          };
        } else if (section === "taskPhotos") {
          updated[section] = prev[section]?.filter(
            (file: any) => file !== imageUrl
          );
        } else if (section === "invoiceReceipt") {
          updated[section] = prev[section]?.filter(
            (file: any) => file.reportHdrInvoiceId !== reportId
          );
        } else if (section === "checkInFile" || section === "checkOutFile" || section === "shopperBagImages") {
          updated[section] = prev[section]?.filter(
            (file: string) => file !== imageUrl
          );
        }

        return updated;
      });
    } catch (error) {
      toast.error("Failed to remove file.");
      console.error("File removal failed:", error);
    }
  };

  const handleNextCategory = async () => {
    if (reportDetails) {
      const questionAnswers = {
        reportHdrId: reportDetails?.reportHeader.reportHeaderId,
        questions: Object.values(answers),
      };
      if (questionAnswers.questions.length > 0) {
        try {
          const responseStep2 = await ApiScoreCardStep2(
            questionAnswers,
            clientGroup
          );
          //toast.success("Category data saved successfully");
        } catch (error) {
          console.error("Error saving category data:", error);
          toast.error("Failed to save category data");
          return; // Don't proceed if save fails
        }
      }

      if (currentCategoryIndex < reportDetails.questions.length - 1) {
        setCurrentCategoryIndex(currentCategoryIndex + 1);
        // Clear validation errors when moving to next category
        setValidationErrors([]);
        setShowValidationErrors(false);
      } else {
        // If we're at the last category, move to the next step
        setCurrentStep(currentStep + 1);
        setCurrentCategoryIndex(0); // Reset category index for next time
      }
    }
  };

  const handlePreviousCategory = () => {
    if (currentCategoryIndex > 0) {
      setCurrentCategoryIndex(currentCategoryIndex - 1);
    } else {
      setCurrentStep(currentStep - 1);
      setCurrentCategoryIndex(0); // Reset category index for next time
    }
  };

  const handleSyncDataToServer = async () => {
    const formattedRatings = Object.entries(ratings).map(([id, score]) => ({
      reportStarRatingId: Number(id), // Convert string key to number
      starRatingScore: score,
    }));
    const formattedBriefJson = Object.entries(textInputs)
      .filter(([key]) => key !== "teamMemberDescription") // Exclude "teamMemberDescription"
      .map(([id, answer]) => ({
        reportBriefJsonId: Number(id), // Convert key to number
        briefJsonAnswer: answer,
      }));

    const formattedAddOnquestions = Object.entries(addOnQuestions).map(
      ([id, answer]) => ({
        reportAddOnQuestionId: Number(id),
        addOnQuestionValue: answer,
      })
    );

    console.log(date);
    const inputDate = new Date(date);
    const formattedDate =
      inputDate.toISOString().replace("T", " ").slice(0, -1) + "000";
    console.log(formattedDate);
    const result = {
      reportHeaderId: reportDetails?.reportHeader.reportHeaderId,
      starRating: formattedRatings,
      scoreCard: {
        inspectionDateTime: formattedDate,
      },
      teamMemberDescription: staffMemberDesc, // Separate extracted value
      briefJson: formattedBriefJson,
      addOnQuestions: formattedAddOnquestions,
    };

    if (result) {
      const responseStep1 = await ApiScoreCardStep1(result, clientGroup);
    }

    const questionAnswers = {
      reportHdrId: reportDetails?.reportHeader.reportHeaderId,
      questions: Object.values(answers),
    };
    if (questionAnswers.questions.length > 0) {
      const responseStep2 = await ApiScoreCardStep2(
        questionAnswers,
        clientGroup
      );
    }
    const data = {
      isNeedToConfirm: false,
      reportHeaderId: reportDetails?.reportHeader.reportHeaderId,
      taskPhotos: uploadedFiles.taskPhotos
        ?.filter((item: any) => {
          if (typeof item !== "object" || item.reportHdrImageId == null) return false;

          const alreadyExists = reportDetails?.taskPhotos?.some(
            (existing: any) => existing.reportHdrImageId === item.reportHdrImageId
          );

          return !alreadyExists;
        })
        .map((item: any) => item.imageUrl),
      invoiceDetails: {
        spentFoodAmt: invoice.food,
        spentBarAmt: invoice.bar,
        spentGamingAmt: invoice.gaming,
        totalSpentAmt: invoice.total,
      },
      invoicePhotos: uploadedFiles.invoiceReceipt
        ?.filter((item: any) => {
          if (typeof item !== "object" || item.reportHdrImageId == null) return false;

          const alreadyExists = reportDetails?.invoicePhotos?.some(
            (existing: any) => existing.reportHdrImageId === item.reportHdrImageId
          );

          return !alreadyExists;
        })
        .map((item: any) => item.imageUrl),
      locationDetails: {
        checkInPhotoUrl: uploadedFiles.checkInFile.join(", "),
        checkOutPhotoUrl: uploadedFiles.checkOutFile.join(", "),
      },
      ...(reportDetails?.reportHeader?.allocationClientGroup === "ALDI" && {
        shopperBagImages: uploadedFiles.shopperBagImages.join(","),
      }),
    };
    if (data) {
      const responseStep3 = await ApiScoreCardStep3(data, clientGroup);
    }
    toast.success("Score card submitted to server.");
    localStorage.removeItem(`isScoreCardEdited_${allocationId}`);
    navigate(-1);
  };

  const handleSubmit = async () => {
    const validationResult = { isValid: true, errors: [] };

    // Step 1
    const step1Result = validateStep1(
      date,
      ratings,
      reportDetails?.starRatingResponseData || [],
      staffMemberDesc,
      textInputs,
      addOnQuestions,
      reportDetails?.briefJsonDataResponse || [],
      reportDetails?.addOnQuestion || []
    );

    // Step 2
    const allCategoriesValidation = [];
    let step2HasErrors = false;
    let errorCategoryIndex = -1;

    if (reportDetails) {
      for (let i = 0; i < reportDetails.questions.length; i++) {
        const categoryValidation = validateStep2(answers, reportDetails, i);
        if (!categoryValidation.isValid) {
          step2HasErrors = true;
          if (errorCategoryIndex === -1) {
            errorCategoryIndex = i; // Store the first category with errors
          }
          allCategoriesValidation.push(...(categoryValidation.errors || []));
        }
      }
    }

    // Step 3
    const step3Result = validateStep3(invoice, uploadedFiles);

    //validationResult.isValid = true;
    const formattedRatings = Object.entries(ratings).map(([id, score]) => ({
      reportStarRatingId: Number(id),
      starRatingScore: score,
    }));

    const formattedBriefJson = Object.entries(textInputs)
      .filter(([key]) => key !== "teamMemberDescription")
      .map(([id, answer]) => ({
        reportBriefJsonId: Number(id),
        briefJsonAnswer: answer,
      }));

    const formattedAddOnquestions = Object.entries(addOnQuestions).map(
      ([id, answer]) => ({
        reportAddOnQuestionId: Number(id),
        addOnQuestionValue: answer,
      })
    );

    const inputDate = new Date(date);
    const formattedDate =
      inputDate.toISOString().replace("T", " ").slice(0, -1) + "000";

    const result = {
      reportHeaderId: reportDetails?.reportHeader.reportHeaderId,
      starRating: formattedRatings,
      scoreCard: {
        inspectionDateTime: formattedDate,
      },
      teamMemberDescription: staffMemberDesc,
      briefJson: formattedBriefJson,
      addOnQuestions: formattedAddOnquestions,
    };

    const questionAnswers = {
      reportHdrId: reportDetails?.reportHeader.reportHeaderId,
      questions: Object.values(answers),
    };

    const data = {
      isNeedToConfirm: false,
      reportHeaderId: reportDetails?.reportHeader.reportHeaderId,
      taskPhotos: uploadedFiles.taskPhotos
        ?.filter((item: any) => {
          if (typeof item !== "object" || item.reportHdrImageId == null) return false;

          const alreadyExists = reportDetails?.taskPhotos?.some(
            (existing: any) => existing.reportHdrImageId === item.reportHdrImageId
          );

          return !alreadyExists;
        })
        .map((item: any) => item.imageUrl),
      invoiceDetails: {
        spentFoodAmt: invoice.food,
        spentBarAmt: invoice.bar,
        spentGamingAmt: invoice.gaming,
        totalSpentAmt: invoice.total,
      },
      invoicePhotos: uploadedFiles.invoiceReceipt
        ?.filter((item: any) => {
          if (typeof item !== "object" || item.reportHdrImageId == null) return false;

          const alreadyExists = reportDetails?.invoicePhotos?.some(
            (existing: any) => existing.reportHdrImageId === item.reportHdrImageId
          );

          return !alreadyExists;
        })
        .map((item: any) => item.imageUrl),
      locationDetails: {
        checkInPhotoUrl: uploadedFiles.checkInFile.join(", "),
        checkOutPhotoUrl: uploadedFiles.checkOutFile.join(", "),
      },
      ...(reportDetails?.reportHeader?.allocationClientGroup === "ALDI" && {
        shopperBagImages: uploadedFiles.shopperBagImages.join(","),
      }),
    };
    const storedData = localStorage.getItem("ScoreCardStep1");
    const resultObject = storedData ? JSON.parse(storedData) : null;

    const questionAnswerData = localStorage.getItem("ScoreCardStep2");
    const questionAnswerObject = questionAnswerData
      ? JSON.parse(questionAnswerData)
      : null;

    try {
      const responseStep1 = await ApiScoreCardStep1(result, clientGroup);
      localStorage.removeItem("ScoreCardStep1");

      if (questionAnswers.questions.length > 0) {
        const responseStep2 = await ApiScoreCardStep2(
          questionAnswers,
          clientGroup
        );
      }
      localStorage.removeItem("ScoreCardStep2");

      const responseStep3 = await ApiScoreCardStep3(data, clientGroup);

      // Clear all localStorage data for this scorecard when successfully submitted
      localStorage.removeItem(`scorecard_${allocationId}`);
      localStorage.removeItem("scoreCardAnswers");
      localStorage.removeItem("scoreCardUploadedFiles");
      localStorage.removeItem("currentStep");
      localStorage.removeItem(`isScoreCardEdited_${allocationId}`);

      toast.success("Score card submitted successfully");
      navigate(-1);
    } catch (error) {
      console.error("Error submitting score card:", error);
      toast.error("Failed to submit score card. Please try again.");
    }
  };

  // Add a useEffect to persist question answers and uploaded files in localStorage when navigating between steps
  useEffect(() => {
    if (currentStep === 2) {
      // Save the current state of answers and uploadedFiles to localStorage
      localStorage.setItem("scoreCardAnswers", JSON.stringify(answers));
      localStorage.setItem(
        "scoreCardUploadedFiles",
        JSON.stringify(uploadedFiles)
      );
    }
  }, [currentStep, answers, uploadedFiles]);

  return (
    <Box className="scoreCardContainer" sx={{ height: "100vh", display: "flex", flexDirection: "column", background: "#fff" }}>

      {/* Top Fixed Header */}
      <Box
        sx={{
          flexShrink: 0,
          px: 1,
          py: 1,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          backgroundColor: "#fff",
          zIndex: 1000,
          boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.05)",
        }}
      >
        <Button
          variant="text"
          sx={{
            color: "#ADB792",
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            fontSize: "14px",
            textTransform: "none",
          }}
          onClick={() => navigate(-1)}
        >
          <span style={{ fontSize: "20px", color: "#ADB792" }}>&larr;</span>
          <span style={{ textDecoration: "underline", fontSize: "18px" }}>Exit report</span>
        </Button>

        <Button
          variant="text"
          onClick={handleSyncDataToServer}
          sx={{
            color: "#000000",
            fontSize: "18px",
            fontWeight: "bold",
            textTransform: "none",
            display: "flex",
            alignItems: "center",
            gap: 0.5,
          }}
          startIcon={<SyncIcon sx={{ fontSize: "16px", color: "#666" }} />}
        >
          Sync Now
        </Button>
      </Box>

      {/* Scrollable Middle Content */}
      <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto", px: 2 }}>
        <ErrorDialog
          open={showValidationErrors && validationErrors.length > 0}
          onClose={() => setShowValidationErrors(false)}
          errors={validationErrors}
          title="Please Fix These Errors"
        />

        <Container maxWidth="lg" sx={{ py: 4 }}>
          {/* Section 1: Rate Your Experience */}
          <Box sx={{ mb: 4 }}>
            <Box
              sx={{
                bgcolor: "#ADB7921A",
                borderRadius: "12px",
                boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
                p: 3,
              }}
            >
              <RateYourExperience
                date={date}
                ratings={ratings} // Ensure ratings is not undefined
                starRatings={reportDetails?.starRatingResponseData || []}
                staffMemberDesc={staffMemberDesc}
                setStaffMemberDesc={setStaffMemberDesc}
                textInputs={textInputs}
                briefJson={reportDetails?.briefJsonDataResponse || []}
                addOnQuestions={addOnQuestions}
                addOnQuestionsData={reportDetails?.addOnQuestion || []}
                reportDetailsHeader={reportDetails?.reportHeader || null}
                reportOtherDetails={reportDetails?.reportOtherDetails}
                setDate={setDate}
                onRatingChange={handleRatingChange}
                onTextChange={handleTextChange}
                onAddOnQuestionChange={handleAddOnQuestionChange}
                validationErrors={[]}
              />
            </Box>
          </Box>

          {/* Section 2: Questions Bank */}
          <Box sx={{ mb: 4 }}>
            <Box
              sx={{
                bgcolor: "#ADB7921A",
                borderRadius: "12px",
                boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
                p: 3,
              }}
            >
              <Typography sx={{ fontWeight: 600, fontSize: 14, mb: 2 }}>
                Questions
              </Typography>
              <Divider sx={{ borderBottomWidth: "2px", borderColor: "#00000033" }} />
              {reportDetails?.questions?.map((category: any, index: number) => (
                <Box key={index} sx={{ mb: 4 }}>
                  <QuestionsBank
                    questionsData={reportDetails}
                    currentCategoryIndex={index}
                    uploadedFiles={uploadedFiles}
                    answers={answers}
                    setAnswer={setAnswers}
                    setUploadedFiles={setUploadedFiles}
                    onAnswerChange={handleAnswerChange}
                    onCommentChange={handleCommentChange}
                    onFileUpload={handleFileUpload}
                    onPreviousCategory={handlePreviousCategory}
                    onNextCategory={handleNextCategory}
                    onRemoveFile={handleRemoveFile}
                    validationErrors={[]}
                    showPreview={true}
                  />
                  {index < reportDetails.questions.length - 1 && (
                    <Divider sx={{ my: 3 }} />
                  )}
                </Box>
              ))}
            </Box>
          </Box>

          {/* Section 3: Other Details */}
          <Box sx={{ mb: 4 }}>
            <Box
              sx={{
                bgcolor: "#ADB7921A",
                borderRadius: "12px",
                boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
                p: 3,
              }}
            >
              <OtherDetailsScoreCard
                invoice={invoice}
                setInvoice={setInvoice}
                reportDetails={reportDetails}
                uploadedFiles={uploadedFiles}
                onFileUpload={handleFileUpload}
                onRemoveFile={handleRemoveFile}
                validationErrors={[]}
              />
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Bottom Fixed Footer */}
      <Box
        sx={{
          flexShrink: 0,
          p: 2,
          backgroundColor: "#fff",
          borderTop: "1px solid #ccc",
          textAlign: "center",
        }}
      >
        <Button
          variant="outlined"
          onClick={() => navigate(-1)}
          sx={{
            borderColor: "#ADB792",
            color: "#ADB792",
            fontWeight: "500",
            px: 4,
            mr: 2,
          }}
        >
          Back to Manage Reports
        </Button>
        <Button
          variant="contained"
          className="button-primary"
          onClick={handleSubmit}
          sx={{ px: 4, fontSize: "14px !important" }}
        >
          Submit Report
        </Button>
      </Box>
    </Box>
  );
};
